import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  ArrowRight,
  Bot,
  Check,
  CreditCard,
  ExternalLink,
  Loader,
  Radio,
  ShieldCheck,
  Sparkles,
  TrendingUp,
} from 'lucide-react';
import { billingAPI, getErrorMessage } from '../../services/api';

type BillingCycle = 'monthly' | 'yearly';

interface Plan {
  id: string;
  name: string;
  tagline: string;
  highlight: boolean;
  price_monthly: number;
  price_yearly: number;
  yearly_savings?: number;
  limits: Record<string, number>;
  features: string[];
}

interface Subscription {
  plan: string;
  plan_name: string;
  billing_cycle: BillingCycle;
  status: string;
  current_period_end: string | null;
  price: number;
}

interface UsageCard {
  key: string;
  label: string;
  description: string;
  unit: string;
  used: number;
  limit: number;
  limit_label: string;
  remaining: number | null;
  percentage: number;
  status: 'healthy' | 'warning' | 'over';
  is_unlimited: boolean;
}

interface BillingOverview {
  subscription: Subscription;
  plans: Plan[];
  usage_cards: UsageCard[];
  ai_summary: {
    total_usd: number;
    call_count: number;
    by_tool: Array<{ key: string; amount_usd: number }>;
  };
  recommendation: {
    plan: string;
    plan_name: string;
    is_upgrade: boolean;
    pressure_keys: string[];
  };
}

const PLAN_ORDER = ['free', 'start', 'smm', 'agency', 'pro'];

const formatMoney = (value: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: value >= 10 ? 0 : 2,
  }).format(value);

const formatNumber = (value: number) =>
  new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(value);

const formatStorage = (mb: number) => {
  if (mb >= 1000) return `${formatNumber(mb / 1000)} GB`;
  return `${formatNumber(mb)} MB`;
};

const formatUsageValue = (card: UsageCard, value: number) => {
  if (card.unit === 'storage_mb') return formatStorage(value);
  return formatNumber(value);
};

const getPlanIndex = (plan: string) => {
  const index = PLAN_ORDER.indexOf(plan);
  return index === -1 ? 0 : index;
};

const statusTone: Record<UsageCard['status'], string> = {
  healthy: 'bg-emerald-500',
  warning: 'bg-amber-500',
  over: 'bg-red-500',
};

export const BillingPage: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [billingCycle, setBillingCycle] = useState<BillingCycle>('monthly');
  const [idempotencyKeys, setIdempotencyKeys] = useState<Record<string, string>>({});

  useEffect(() => {
    if (searchParams.get('success') === 'true') {
      toast.success(t('billing_subscription_activated', 'Subscription activated'));
    } else if (searchParams.get('canceled') === 'true') {
      toast(t('billing_checkout_canceled', 'Checkout was canceled'));
    }
  }, [searchParams, t]);

  const { data: overview, isLoading } = useQuery({
    queryKey: ['billing-overview'],
    queryFn: () => billingAPI.overview().then((res) => res.data as BillingOverview),
  });

  const checkoutMutation = useMutation({
    mutationFn: async (plan: string) => {
      try {
        let idempotencyKey = idempotencyKeys[plan];
        if (!idempotencyKey) {
          idempotencyKey = crypto.randomUUID();
          setIdempotencyKeys((prev) => ({ ...prev, [plan]: idempotencyKey }));
        }
        const res = await billingAPI.checkout(plan, billingCycle, idempotencyKey);
        return { type: 'checkout' as const, data: res.data };
      } catch (err: unknown) {
        const status = (err as { response?: { status?: number } })?.response?.status;
        if (status === 503) {
          const res = await billingAPI.devUpgrade(plan, billingCycle);
          return { type: 'dev' as const, data: res.data };
        }
        throw err;
      }
    },
    onSuccess: (result) => {
      if (result.type === 'checkout') {
        window.location.href = result.data.checkout_url;
        return;
      }
      toast.success(
        t('billing_upgraded', 'Upgraded to {{plan}}!', { plan: result.data.plan }),
      );
      window.location.reload();
    },
    onError: (error: unknown) => {
      toast.error(
        getErrorMessage(error, t('billing_checkout_failed', 'Failed to start checkout')),
      );
    },
  });

  const portalMutation = useMutation({
    mutationFn: () => billingAPI.portal(),
    onSuccess: (data) => {
      window.location.href = data.data.portal_url;
    },
    onError: (error: unknown) => {
      toast.error(
        getErrorMessage(error, t('billing_portal_failed', 'Failed to open billing portal')),
      );
    },
  });

  if (isLoading || !overview) {
    return (
      <div className="min-h-[calc(100vh-72px)] bg-n-page flex items-center justify-center">
        <div className="rounded-n border border-n-border bg-n-card px-6 py-5 text-center shadow-n-sm">
          <Loader className="mx-auto mb-3 h-7 w-7 animate-spin text-n-text-md" />
          <p className="text-sm font-semibold text-n-text">
            {t('billing_loading_plans', 'Loading plans...')}
          </p>
        </div>
      </div>
    );
  }

  const { subscription, plans, usage_cards, ai_summary, recommendation } = overview;
  const currentPlanIndex = getPlanIndex(subscription.plan);
  const aiCard = usage_cards.find((card) => card.key === 'ai_generations');
  const activePressureCards = usage_cards.filter((card) => card.status !== 'healthy');
  const primaryPlan = plans.find((plan) => plan.id === recommendation.plan) || plans[0];

  return (
    <div className="min-h-[calc(100vh-72px)] bg-n-page px-4 py-4 md:px-6">
      <div className="mx-auto flex max-w-[1240px] flex-col gap-4">
        <section className="rounded-n border border-n-border bg-n-card p-5 shadow-n-sm">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="max-w-2xl">
              <div className="mb-3 inline-flex items-center gap-2 rounded-n-sm border border-n-border bg-n-hover px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-n-text-md">
                <Sparkles className="h-3.5 w-3.5 text-emerald-700" />
                {t('billing_revenue_workspace_label', 'Billing workspace')}
              </div>
              <h1 className="text-2xl font-semibold tracking-tight text-n-text md:text-3xl">
                {t('billing_revenue_workspace_title', 'Usage, AI credits, and plans')}
              </h1>
              <p className="mt-2 max-w-2xl text-[13px] leading-6 text-n-text-md">
                {t(
                  'billing_revenue_workspace_desc',
                  'See what your team is using, where AI cost is going, and which plan fits before you upgrade.',
                )}
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <div className="inline-flex rounded-n-sm border border-n-border bg-n-page p-0.5">
                {(['monthly', 'yearly'] as const).map((cycle) => (
                  <button
                    key={cycle}
                    type="button"
                    onClick={() => setBillingCycle(cycle)}
                    className={`rounded-n-xs px-3 py-1.5 text-[13px] font-semibold transition ${
                      billingCycle === cycle
                        ? 'bg-n-card text-n-text shadow-n-sm'
                        : 'text-n-text-md hover:text-n-text'
                    }`}
                  >
                    {cycle === 'monthly'
                      ? t('billing_monthly', 'Monthly')
                      : t('billing_yearly', 'Yearly')}
                  </button>
                ))}
              </div>
              {subscription.plan !== 'free' && (
                <button
                  type="button"
                  onClick={() => portalMutation.mutate()}
                  disabled={portalMutation.isPending}
                  className="inline-flex items-center gap-2 rounded-n-sm border border-n-border bg-n-card px-3.5 py-2 text-[13px] font-semibold text-n-text shadow-n-sm transition hover:bg-n-hover disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {portalMutation.isPending ? (
                    <Loader className="h-4 w-4 animate-spin" />
                  ) : (
                    <ExternalLink className="h-4 w-4" />
                  )}
                  {t('billing_manage_billing_btn', 'Manage Billing')}
                </button>
              )}
            </div>
          </div>
        </section>

        <section className="grid gap-4 xl:grid-cols-[1.05fr_0.95fr]">
          <div className="grid gap-4 md:grid-cols-3">
            <div className="rounded-n border border-n-border bg-n-card p-4 shadow-n-sm">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-n-text-lt">
                    {t('billing_current_plan_label', 'Current Plan')}
                  </p>
                  <h2 className="mt-1.5 text-2xl font-semibold tracking-tight text-n-text">
                    {subscription.plan_name}
                  </h2>
                </div>
                <ShieldCheck className="h-6 w-6 text-emerald-600" />
              </div>
              <div className="mt-4 rounded-n-sm border border-n-border bg-n-page p-3">
                <p className="text-[13px] font-semibold capitalize text-n-text">
                  {subscription.status}
                </p>
                <p className="mt-1 text-xs text-n-text-md">
                  {subscription.current_period_end
                    ? t('billing_renews_on', 'Renews {{date}}', {
                        date: new Date(subscription.current_period_end).toLocaleDateString(),
                      })
                    : t('billing_no_renewal_date', 'No renewal date yet')}
                </p>
              </div>
            </div>

            <div className="rounded-n border border-n-border bg-n-card p-4 shadow-n-sm">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-n-text-lt">
                    {t('billing_ai_credits_label', 'AI credits')}
                  </p>
                  <h2 className="mt-1.5 text-2xl font-semibold tracking-tight text-n-text">
                    {aiCard ? formatUsageValue(aiCard, aiCard.used) : '0'}
                  </h2>
                </div>
                <Bot className="h-6 w-6 text-emerald-600" />
              </div>
              <p className="mt-3 text-[13px] leading-5 text-n-text-md">
                {aiCard
                  ? t('billing_ai_credits_hint', '{{used}} of {{limit}} used this month', {
                      used: formatUsageValue(aiCard, aiCard.used),
                      limit: aiCard.limit_label,
                    })
                  : t('billing_ai_credits_empty', 'No AI credit usage yet.')}
              </p>
            </div>

            <div className="rounded-n border border-n-border bg-n-card p-4 shadow-n-sm">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-n-text-lt">
                    {t('billing_ai_spend_label', 'AI spend')}
                  </p>
                  <h2 className="mt-1.5 text-2xl font-semibold tracking-tight text-n-text">
                    {formatMoney(ai_summary.total_usd || 0)}
                  </h2>
                </div>
                <TrendingUp className="h-6 w-6 text-emerald-600" />
              </div>
              <Link
                to="/ai-usage"
                className="mt-3 inline-flex items-center gap-1.5 text-[13px] font-semibold text-emerald-700 hover:text-emerald-900"
              >
                {t('billing_view_ai_usage', 'View AI usage')}
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>

          <div className="rounded-n border border-n-border bg-n-card p-4 shadow-n-sm">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-n-text-lt">
                  {t('billing_plan_recommendation', 'Recommendation')}
                </p>
                <h2 className="mt-1.5 text-xl font-semibold tracking-tight text-n-text">
                  {recommendation.is_upgrade
                    ? t('billing_recommend_upgrade', 'Move to {{plan}}', {
                        plan: recommendation.plan_name,
                      })
                    : t('billing_recommend_stay', 'Your plan still fits')}
                </h2>
              </div>
              <Radio className="h-6 w-6 text-emerald-600" />
            </div>
            <p className="mt-2 text-[13px] leading-6 text-n-text-md">
              {recommendation.is_upgrade
                ? t(
                    'billing_recommend_upgrade_desc',
                    'Your current usage is close to one or more limits. Upgrade before publishing, AI, or page work gets blocked.',
                  )
                : t(
                    'billing_recommend_stay_desc',
                    'No urgent pressure found. Keep monitoring AI credits and scheduled posts as your team grows.',
                  )}
            </p>
            {primaryPlan && recommendation.is_upgrade && (
              <button
                type="button"
                onClick={() => checkoutMutation.mutate(primaryPlan.id)}
                disabled={checkoutMutation.isPending}
                className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-n-sm bg-n-text px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-black disabled:cursor-not-allowed disabled:opacity-60"
              >
                {checkoutMutation.isPending ? (
                  <Loader className="h-4 w-4 animate-spin" />
                ) : (
                  <CreditCard className="h-4 w-4" />
                )}
                {t('billing_upgrade_to_plan', 'Upgrade to {{plan}}', {
                  plan: primaryPlan.name,
                })}
              </button>
            )}
          </div>
        </section>

        {activePressureCards.length > 0 && (
          <section className="rounded-n-sm border border-amber-200 bg-amber-50 px-4 py-3 text-[13px] font-semibold text-amber-900">
            {t(
              'billing_pressure_notice',
              '{{count}} usage area needs attention before it blocks work.',
              { count: activePressureCards.length },
            )}
          </section>
        )}

        <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {usage_cards.map((card) => (
            <article
              key={card.key}
              className="rounded-n border border-n-border bg-n-card p-4 shadow-n-sm"
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="text-sm font-semibold text-n-text">{card.label}</h3>
                  <p className="mt-1 text-xs leading-5 text-n-text-md">
                    {card.description}
                  </p>
                </div>
                <span
                  className={`mt-1 h-2 w-2 rounded-full ${statusTone[card.status]}`}
                  aria-label={card.status}
                />
              </div>
              <div className="mt-4 flex items-end justify-between gap-4">
                <p className="text-xl font-semibold tracking-tight text-n-text">
                  {formatUsageValue(card, card.used)}
                </p>
                <p className="pb-0.5 text-[13px] font-semibold text-n-text-md">
                  / {card.is_unlimited ? card.limit_label : formatUsageValue(card, card.limit)}
                </p>
              </div>
              <div className="mt-4 h-2 overflow-hidden rounded-full bg-n-page">
                <div
                  className={`h-full rounded-full ${statusTone[card.status]}`}
                  style={{ width: `${card.is_unlimited ? 18 : Math.max(card.percentage, 4)}%` }}
                />
              </div>
            </article>
          ))}
        </section>

        <section className="rounded-n border border-n-border bg-n-card p-5 shadow-n-sm">
          <div className="mb-5 flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-n-text-lt">
                {t('billing_plans_label', 'Plans')}
              </p>
              <h2 className="mt-1.5 text-xl font-semibold tracking-tight text-n-text">
                {t('billing_choose_plan_title', 'Choose by work volume, not guesswork')}
              </h2>
            </div>
            <p className="max-w-md text-sm leading-6 text-n-text-md">
              {t(
                'billing_choose_plan_desc',
                'AI credits, accounts, pages, links, storage, and seats are visible before you pay.',
              )}
            </p>
          </div>

          <div className="grid gap-3 lg:grid-cols-5">
            {plans.map((plan) => {
              const price =
                billingCycle === 'yearly' ? plan.price_yearly / 12 : plan.price_monthly;
              const isCurrent = plan.id === subscription.plan;
              const isUpgrade = getPlanIndex(plan.id) > currentPlanIndex;
              return (
                <article
                  key={plan.id}
                  className={`flex min-h-full flex-col rounded-n-sm border p-4 transition ${
                    isCurrent
                      ? 'border-emerald-300 bg-emerald-50'
                      : plan.highlight
                        ? 'border-n-border bg-n-page'
                        : 'border-n-border bg-n-card'
                  }`}
                >
                  <div className="mb-5">
                    <div className="flex items-center justify-between gap-3">
                      <h3 className="text-lg font-semibold text-n-text">{plan.name}</h3>
                      {(isCurrent || plan.highlight) && (
                        <span className="rounded-n-xs bg-white px-2 py-0.5 text-[10px] font-semibold text-emerald-800">
                          {isCurrent
                            ? t('billing_current_plan', 'Current Plan')
                            : t('billing_most_popular', 'Most Popular')}
                        </span>
                      )}
                    </div>
                    <p className="mt-2 text-sm leading-5 text-n-text-md">{plan.tagline}</p>
                    <div className="mt-5">
                      <span className="text-2xl font-semibold tracking-tight text-n-text">
                        {price === 0 ? t('billing_free', 'Free') : formatMoney(price)}
                      </span>
                      {price > 0 && (
                        <span className="ml-1 text-sm font-bold text-n-text-md">
                          /{t('billing_per_month', 'mo')}
                        </span>
                      )}
                    </div>
                    {billingCycle === 'yearly' && Boolean(plan.yearly_savings) && (
                      <p className="mt-1 text-xs font-bold text-emerald-700">
                        {t('billing_save_yearly', 'Save {{amount}} yearly', {
                          amount: formatMoney(plan.yearly_savings || 0),
                        })}
                      </p>
                    )}
                  </div>

                  <ul className="flex-1 space-y-2">
                    {plan.features.slice(0, 7).map((feature) => (
                      <li key={feature} className="flex gap-2 text-[13px] text-n-text-md">
                        <Check className="mt-0.5 h-4 w-4 flex-none text-emerald-600" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <button
                    type="button"
                    disabled={isCurrent || checkoutMutation.isPending || plan.id === 'free'}
                    onClick={() => checkoutMutation.mutate(plan.id)}
                    className={`mt-5 inline-flex w-full items-center justify-center rounded-n-sm px-3 py-2 text-[13px] font-semibold transition disabled:cursor-not-allowed disabled:opacity-60 ${
                      isCurrent
                        ? 'bg-white text-n-text'
                        : isUpgrade
                          ? 'bg-n-text text-white hover:bg-black'
                          : 'border border-n-border bg-white text-n-text hover:bg-n-page'
                    }`}
                  >
                    {isCurrent
                      ? t('billing_current_plan_btn', 'Current Plan')
                      : isUpgrade
                        ? t('billing_upgrade_btn', 'Upgrade')
                        : t('billing_downgrade_btn', 'Downgrade')}
                  </button>
                </article>
              );
            })}
          </div>
        </section>
      </div>
    </div>
  );
};

export default BillingPage;
